import React, { useContext } from 'react';
import { BrowserRouter, Switch, Route, Redirect, useLocation } from 'react-router-dom';
import StatusPage from '../../Pages/StatusPage';
import OrdersPage from '../../Pages/OrdersPage';
import DevJobsSotPage from '../../Pages/OrdersPage/DevJobsSotPage';
import VacationPage from '../../Pages/VacationPage';
import SettingsPage from '../../Pages/SettingsPage';
import SignupPage from '../../Pages/SignupPage';
import LoginPage from '../../Pages/LoginPage';
import OpenJobPage from '../../Pages/OpenJobPage';
import Navigation from '../Navigation';
import { AuthContext } from '../../Providers/UserProvider';
import LoaderComponent from '../Loader';
import ProjectsListPage from '../../Pages/ProjectsListPage';
import ProjectDetailPage from '../../Pages/ProjectDetailPage';
import { PWA_JOBS_ALIAS_PATH, PWA_JOBS_DEV_PATH, PWA_JOBS_PATH } from '../../Pages/OrdersPage/ordersSurface';
import { isOpenJobReturnPath } from '../../Pages/OpenJobPage/openJobRoute';

const SIGN_IN_RETURN_PATHS = [PWA_JOBS_PATH, PWA_JOBS_ALIAS_PATH, PWA_JOBS_DEV_PATH];

function signInReturnTarget(state: unknown): { pathname: string; search: string; hash: string } | null {
    const from = (state as { from?: { pathname?: unknown; search?: unknown; hash?: unknown } } | null)?.from;
    if (!from || typeof from.pathname !== 'string') return null;
    if (!SIGN_IN_RETURN_PATHS.includes(from.pathname) && !isOpenJobReturnPath(from.pathname)) return null;
    return {
        pathname: from.pathname,
        search: typeof from.search === 'string' ? from.search : '',
        hash: typeof from.hash === 'string' ? from.hash : '',
    };
}

function SignedOutLoginRedirect() {
    const location = useLocation();
    return (
        <Redirect
            to={{
                pathname: '/login',
                state: {
                    from: {
                        pathname: location.pathname,
                        search: location.search,
                        hash: location.hash,
                    },
                },
            }}
        />
    );
}

function SignedInFallback() {
    const location = useLocation();
    const from = signInReturnTarget(location.state);
    if (from) {
        return <Redirect to={{ pathname: from.pathname, search: from.search, hash: from.hash }} />;
    }
    return <Redirect to="/app/orders" />;
}

export function AppRoutes({
    techId = null,
    user = null,
}: {
    techId?: string | null;
    user?: unknown;
}) {
    const { error } = useContext(AuthContext);
    return techId && user ? (
        <>
            {error ? (
                <div className="py-4 bg-primaryTheme w-full text-white text-center mb-3">{error}</div>
            ) : null}
            <Navigation />
            <Switch>
                <Route exact path="/open-jobs/:regionId/:jobId" component={OpenJobPage} />
                <Route path="/open-jobs/:regionId/:techId/:jobId" component={OpenJobPage} />
                <Route path="/app/status" component={StatusPage} />
                <Route path={PWA_JOBS_PATH} component={OrdersPage} />
                <Route
                    path={PWA_JOBS_ALIAS_PATH}
                    render={({ location }) => (
                        <Redirect to={{ pathname: PWA_JOBS_PATH, search: location.search, hash: location.hash }} />
                    )}
                />
                <Route path={PWA_JOBS_DEV_PATH} component={DevJobsSotPage} />
                <Route path="/app/vacation" component={VacationPage} />
                <Route path="/app/settings" component={SettingsPage} />
                <Route path="/app/projects/:projectId/:techProjectId" component={ProjectDetailPage} />
                <Route path="/app/projects" component={ProjectsListPage} />
                <SignedInFallback />
            </Switch>
        </>
    ) : (
        <Switch>
            <Route exact path="/open-jobs/:regionId/:jobId" component={OpenJobPage} />
            <Route path="/open-jobs/:regionId/:techId/:jobId" component={OpenJobPage} />
            <Route path="/signup" component={SignupPage} />
            <Route path="/login" component={LoginPage} />
            <SignedOutLoginRedirect />
        </Switch>
    );
}

function Router() {
    const { techId, user, initialized } = useContext(AuthContext);

    return (
        <div>
            {initialized ? (
                <BrowserRouter>
                    <AppRoutes techId={techId} user={user} />
                </BrowserRouter>
            ) : (
                <LoaderComponent showLoader />
            )}
        </div>
    );
}
export default Router;
